import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import styles from './UnitDetails.module.css';
import { updateUnit, deleteUnit, removeUnitCustomer } from '../services/unitService';
import CustomerProfile from './CustomerProfile';
// import { fetchCustomerDetails } from '../services/customerService';
import { showSuccessNotification, showErrorNotification } from '../store/slices/notificationSlice';

const UnitDetails = ({ unit, onBack, onEdit, onDelete, onAddCustomer, onShowUpdates, onRefresh }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Get unit permissions from cached permissions
    const {
        hasUpdateUnit,
        hasDeleteUnit,
        hasAddCustomerUnit,
        hasRemoveCustomerUnit,
        hasShowUnitUpdates,
        hasShowCustomer,
        hasUpdateInstallment,
    } = useCachedPermissions();

    const [isEditMode, setIsEditMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isUnlinking, setIsUnlinking] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [unlinkError, setUnlinkError] = useState('');
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [customerData, setCustomerData] = useState(null);
    const [loadingCustomer, setLoadingCustomer] = useState(false);
    const [customerError, setCustomerError] = useState('');
    const [editData, setEditData] = useState({
        label: unit?.name || unit?.title || unit?.label || '',
        unitID: unit?.unitID || '',
        unit_type: unit?.unit_type || 'Residential',
        building: unit?.building || '',
        unit_no: unit?.unit_no || '',
        floor: unit?.floor || '',
        price: unit?.price || '',
        description: unit?.description || '',
        location: unit?.location || '',
        is_available: unit?.is_available !== undefined ? unit.is_available : (unit?.status === 'available')
    });
    // Use images from unit data or fallback to mock images
    const [images, setImages] = useState(() => {
        if (unit?.images && unit.images.length > 0) {
            return unit.images.map((image, index) => ({
                id: index + 1,
                src: image,
                alt: `Unit Image ${index + 1}`,
                isOriginal: true, // Mark original images
                originalSrc: image // Keep reference to original URL
            }));
        }
        return [
            { id: 1, src: unit?.image1 || '/src/assets/images/mock_unit_img.png', alt: 'Unit Image 1', isOriginal: true, originalSrc: unit?.image1 || '/src/assets/images/mock_unit_img.png' },
            { id: 2, src: unit?.image2 || '/src/assets/images/mock_details_image_1.png', alt: 'Unit Image 2', isOriginal: true, originalSrc: unit?.image2 || '/src/assets/images/mock_details_image_1.png' },
            { id: 3, src: unit?.image3 || '/src/assets/images/mock_details_image_2.png', alt: 'Unit Image 3', isOriginal: true, originalSrc: unit?.image3 || '/src/assets/images/mock_details_image_2.png' },
            { id: 4, src: unit?.image4 || '/src/assets/images/mock_details_image_3.png', alt: 'Unit Image 4', isOriginal: true, originalSrc: unit?.image4 || '/src/assets/images/mock_details_image_3.png' }
        ];
    });

    // Track new image files for upload
    const [newImageFiles, setNewImageFiles] = useState([]);

    // Update images when unit data changes
    useEffect(() => {
        if (unit?.images && unit.images.length > 0) {
            const unitImages = unit.images.map((image, index) => ({
                id: index + 1,
                src: image,
                alt: `Unit Image ${index + 1}`,
                isOriginal: true,
                originalSrc: image
            }));
            setImages(unitImages);
            setNewImageFiles([]); // Reset new images when unit changes
        }
    }, [unit]);

    // // Fetch customer data if unit is unavailable and has user_id
    // useEffect(() => {
    //     const fetchCustomer = async () => {
    //         if (unit?.user_id && !unit?.is_available) {
    //             setLoadingCustomer(true);
    //             setCustomerError('');
    //             try {
    //                 const response = await fetchCustomerDetails(unit.user_id);
    //                 // Extract the actual customer data from the response
    //                 setCustomerData(response.data || response);
    //             } catch (error) {
    //                 console.error('Failed to fetch customer details:', error);
    //                 setCustomerError(`Failed to load customer details: ${error.message}`);
    //             } finally {
    //                 setLoadingCustomer(false);
    //             }
    //         } else {
    //             setCustomerData(null);
    //             setCustomerError('');
    //         }
    //     };

    //     fetchCustomer();
    // }, [unit?.user_id, unit?.is_available]);

    const handleEditClick = () => {
        setIsEditMode(true);
        setEditData({
            label: unit?.name || unit?.title || unit?.label || '',
            unitID: unit?.unitID || '',
            unit_type: unit?.unit_type || 'Residential',
            building: unit?.building || '',
            unit_no: unit?.unit_no || '',
            floor: unit?.floor || '',
            price: unit?.price || '',
            description: unit?.description || '',
            location: unit?.location || '',
        });
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            console.log('Saving unit changes:', editData);

            // Prepare FormData for the request
            const formData = new FormData();

            // Add basic unit data
            formData.append('label', editData.label);
            formData.append('unitID', editData.unitID);
            formData.append('unit_type', editData.unit_type);
            formData.append('building', editData.building);
            formData.append('unit_no', editData.unit_no);
            formData.append('floor', editData.floor);
            formData.append('price', editData.price);
            formData.append('description', editData.description);
            formData.append('location', editData.location);

            // Get old images that are still present (not removed)
            const remainingOldImages = images
                .filter(img => img.isOriginal)
                .map(img => img.originalSrc);

            // Add old images that weren't removed
            if (remainingOldImages.length > 0) {
                remainingOldImages.forEach((imageUrl, index) => {
                    formData.append(`old_img`, imageUrl);
                });
            }

            // Add new image files
            newImageFiles.forEach((file, index) => {
                formData.append(`image`, file);
            });

            console.log('Sending FormData with old images:', remainingOldImages);
            console.log('Sending FormData with new images:', newImageFiles.length);

            const response = await updateUnit(unit.id, formData);
            console.log('Unit updated successfully:', response);

            // Update local unit data with the edited values
            const updatedUnit = {
                ...unit,
                name: editData.label,
                title: editData.label,
                label: editData.label,
                unitID: editData.unitID,
                unit_type: editData.unit_type,
                building: editData.building,
                unit_no: editData.unit_no,
                floor: editData.floor,
                price: parseFloat(editData.price) || 0,
                description: editData.description,
                location: editData.location
            };

            // Call the parent onEdit callback with updated data
            if (onEdit) {
                onEdit(updatedUnit);
            }

            setIsEditMode(false);
            setNewImageFiles([]); // Reset new image files
            dispatch(showSuccessNotification('Unit updated successfully!'));

            if (onBack) {
                onBack();
            }
        } catch (error) {
            console.error('Failed to update unit:', error);
            dispatch(showErrorNotification(`Failed to update unit: ${error.message}`));
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setIsEditMode(false);
        setEditData({
            label: unit?.name || unit?.title || unit?.label || '',
            unitID: unit?.unitID || '',
            unit_type: unit?.unit_type || 'Residential',
            building: unit?.building || '',
            unit_no: unit?.unit_no || '',
            floor: unit?.floor || '',
            price: unit?.price || '',
            description: unit?.description || '',
            location: unit?.location || '',
            is_available: unit?.is_available !== undefined ? unit.is_available : (unit?.status === 'available')
        });

        // Reset images to original state
        if (unit?.images && unit.images.length > 0) {
            const originalImages = unit.images.map((image, index) => ({
                id: index + 1,
                src: image,
                alt: `Unit Image ${index + 1}`,
                isOriginal: true,
                originalSrc: image
            }));
            setImages(originalImages);
        } else {
            setImages([
                { id: 1, src: unit?.image1 || '/src/assets/images/mock_unit_img.png', alt: 'Unit Image 1', isOriginal: true, originalSrc: unit?.image1 || '/src/assets/images/mock_unit_img.png' },
                { id: 2, src: unit?.image2 || '/src/assets/images/mock_details_image_1.png', alt: 'Unit Image 2', isOriginal: true, originalSrc: unit?.image2 || '/src/assets/images/mock_details_image_1.png' },
                { id: 3, src: unit?.image3 || '/src/assets/images/mock_details_image_2.png', alt: 'Unit Image 3', isOriginal: true, originalSrc: unit?.image3 || '/src/assets/images/mock_details_image_2.png' },
                { id: 4, src: unit?.image4 || '/src/assets/images/mock_details_image_3.png', alt: 'Unit Image 4', isOriginal: true, originalSrc: unit?.image4 || '/src/assets/images/mock_details_image_3.png' }
            ]);
        }

        // Reset new image files
        setNewImageFiles([]);
    };

    const handleFieldChange = (field, value) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAssignCustomer = () => {
        if (onAddCustomer) {
            onAddCustomer(unit);
        }
    };

    const handleUnlinkCustomer = async () => {
        if (!window.confirm('Are you sure you want to unlink this customer from the unit? This action cannot be undone.')) {
            return;
        }

        setIsUnlinking(true);
        setUnlinkError('');

        try {
            await removeUnitCustomer(unit.id);
            dispatch(showSuccessNotification('Customer has been successfully unlinked from the unit'));
            // Call the refresh callback to fetch updated data
            if (onRefresh) {
                await onRefresh();
            } else if (onBack) {
                onBack(); // Fallback to going back if no refresh callback
            } else {
                // Last resort: refresh the page
                window.location.reload();
            }
        } catch (error) {
            console.error('Failed to unlink customer:', error);
            const errorMessage = error.message || 'Failed to unlink customer from unit';
            setUnlinkError(errorMessage);
            dispatch(showErrorNotification(`Failed to unlink customer: ${errorMessage}`));
        } finally {
            setIsUnlinking(false);
        }
    };

    const handleViewCustomerProfile = (id) => {
        if (id) {
            setSelectedCustomerId(id);
        }
    };

    const handleBackFromCustomer = () => {
        setSelectedCustomerId(null);
    };

    const handleCustomerEdit = (updatedCustomer) => {
        // Handle customer edit if needed
        console.log('Customer updated from UnitDetails:', updatedCustomer);
    };

    const handleCustomerDelete = (customerId) => {
        // Handle customer delete if needed
        console.log('Customer deleted from UnitDetails:', customerId);
        setSelectedCustomerId(null); // Go back to unit details
    };

    const handleDeleteUnit = async () => {
        if (!window.confirm('Are you sure you want to delete this unit? This action cannot be undone.')) {
            return;
        }

        setIsDeleting(true);
        setDeleteError('');

        try {
            await deleteUnit(unit.id);
            console.log('Unit deleted successfully');

            dispatch(showSuccessNotification('Unit deleted successfully!'));

            // Call the parent onDelete callback if provided
            if (onDelete) {
                onDelete(unit.id);
            }

            // Go back to units list after successful deletion
            if (onBack) {
                onBack();
            }
        } catch (error) {
            console.error('Failed to delete unit:', error);
            setDeleteError(error.message || 'Failed to delete unit');
            dispatch(showErrorNotification(`Failed to delete unit: ${error.message || 'Unknown error'}`));
        } finally {
            setIsDeleting(false);
        }
    };

    const removeImage = (imageId) => {
        const imageToRemove = images.find(img => img.id === imageId);

        if (imageToRemove) {
            // If it's a new image (not original), also remove it from newImageFiles
            if (!imageToRemove.isOriginal && imageToRemove.file) {
                setNewImageFiles(prev => prev.filter(file => file !== imageToRemove.file));
            }

            // Remove from images array
            setImages(prev => prev.filter(img => img.id !== imageId));
        }
    };

    const addImage = () => {
        // Create a hidden file input
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.multiple = true;

        fileInput.onchange = (e) => {
            const files = Array.from(e.target.files);
            files.forEach(file => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const newId = Math.max(...images.map(img => img.id), 0) + 1;

                    // Add to images for display
                    setImages(prev => [...prev, {
                        id: newId,
                        src: event.target.result,
                        alt: `Unit Image ${newId}`,
                        isOriginal: false, // Mark as new image
                        file: file // Keep reference to the file
                    }]);

                    // Add to new image files for upload
                    setNewImageFiles(prev => [...prev, file]);
                };
                reader.readAsDataURL(file);
            });
        };

        fileInput.click();
    };

    if (!unit) {
        return (
            <div className={styles.noUnitSelected}>
                <h3>No Unit Selected</h3>
                <p>Please select a unit to view its details.</p>
            </div>
        );
    }

    // If a customer is selected, show the customer profile
    if (selectedCustomerId) {
        return (
            <CustomerProfile
                customerId={selectedCustomerId}
                onBack={handleBackFromCustomer}
                onEdit={handleCustomerEdit}
                onDelete={handleCustomerDelete}
            />
        );
    }

    return (
        <div className={styles.unitDetailsContainer}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backButton} onClick={onBack}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5" />
                            <path d="M12 19l-7-7 7-7" />
                        </svg>
                        Back
                    </button>
                    <span className={styles.unitTitle}>
                        {unit.status} {unit.category}
                    </span>
                </div>
                <div className={styles.headerActions}>
                    {isEditMode ? (
                        <>
                            <button className={styles.cancelButton} onClick={handleCancel}>
                                Cancel
                            </button>
                            <button
                                className={`${styles.saveButton} ${isSaving ? styles.disabled : ''}`}
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        </>
                    ) : (
                        <div className={styles.headerActions}>
                            {hasUpdateUnit && (
                                <button
                                    className={styles.editButton}
                                    onClick={handleEditClick}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                    Edit
                                </button>
                            )}
                            {hasShowUnitUpdates && (
                                <button
                                    className={styles.editButton}
                                    onClick={() => onShowUpdates && onShowUpdates(unit)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M19 12H5" />
                                        <path d="M12 19l-7-7 7-7" />
                                    </svg>
                                    Updates
                                </button>
                            )}
                            {hasDeleteUnit && (
                                <button
                                    className={`${styles.deleteButton} ${isDeleting ? styles.disabled : ''}`}
                                    onClick={handleDeleteUnit}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? (
                                        <>
                                            <svg className={styles.spinner} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 12a9 9 0 11-6.219-8.56" />
                                            </svg>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3,6 5,6 21,6"></polyline>
                                                <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                                <line x1="14" y1="11" x2="14" y2="17"></line>
                                            </svg>
                                            Delete
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {deleteError && (
                <div className={styles.errorMessage}>
                    <p>{deleteError}</p>
                    <button
                        className={styles.dismissError}
                        onClick={() => setDeleteError('')}
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {/* Main Content */}
            <div className={styles.content}>
                <div className={styles.leftContent}>
                    <div className={styles.unitInfo}>
                        <h2 className={styles.unitTitle}>
                            {isEditMode ? (
                                <div className={styles.editField}>
                                    <label>Label</label>
                                    <div className={styles.fieldWithIcon}>
                                        <input
                                            type="text"
                                            value={editData.label}
                                            onChange={(e) => handleFieldChange('label', e.target.value)}
                                            className={styles.editInput}
                                        />
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                        </svg>
                                    </div>
                                </div>
                            ) : (
                                editData.label || unit.name || unit.title || unit.label
                            )}
                        </h2>
                        <div className={styles.priceSection}>
                            <label>Price</label>
                            {isEditMode ? (
                                <div className={styles.fieldWithIcon}>
                                    <input
                                        type="number"
                                        value={editData.price}
                                        onChange={(e) => handleFieldChange('price', e.target.value)}
                                        className={styles.editInput}
                                    />
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                </div>
                            ) : (
                                <span className={styles.priceValue}>{editData.price ? editData.price.toLocaleString() : (unit.price ? unit.price.toLocaleString() : 'N/A')}</span>
                            )}
                        </div>
                        <div className={styles.unitTypeSection}>
                            <label>Unit Type</label>
                            {isEditMode ? (
                                <div className={styles.fieldWithIcon}>
                                    <select
                                        value={editData.unit_type}
                                        onChange={(e) => handleFieldChange('unit_type', e.target.value)}
                                        className={styles.editInput}
                                    >
                                        <option value="Residential">Residential</option>
                                        <option value="Chalet">Chalet</option>
                                        <option value="Commercial">Commercial</option>
                                        <option value="Administration">Administration</option>
                                    </select>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                </div>
                            ) : (
                                <span className={styles.unitTypeValue}>{editData.unit_type || unit.unit_type || 'N/A'}</span>
                            )}
                        </div>

                        <div className={styles.unitIdSection}>
                            <label>Unit ID</label>
                            {isEditMode ? (
                                <div className={styles.fieldWithIcon}>
                                    <input
                                        type="text"
                                        value={editData.unitID}
                                        onChange={(e) => handleFieldChange('unitID', e.target.value)}
                                        className={styles.editInput}
                                    />
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                </div>
                            ) : (
                                <span className={styles.unitIdValue}>{editData.unitID || unit.unitID || 'N/A'}</span>
                            )}
                        </div>


                        <div className={styles.locationSection}>
                            <label>Location</label>
                            {isEditMode ? (
                                <div className={styles.fieldWithIcon}>
                                    <input
                                        type="text"
                                        value={editData.location}
                                        onChange={(e) => handleFieldChange('location', e.target.value)}
                                        className={styles.editInput}
                                    />
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                </div>
                            ) : (
                                <span className={styles.locationValue}>{editData.location || unit.location || 'N/A'}</span>
                            )}
                        </div>
                        <div className={styles.buildingSection}>
                            <label>Building</label>
                            {isEditMode ? (
                                <div className={styles.fieldWithIcon}>
                                    <input
                                        type="text"
                                        value={editData.building}
                                        onChange={(e) => handleFieldChange('building', e.target.value)}
                                        className={styles.editInput}
                                    />
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                </div>
                            ) : (
                                <span className={styles.buildingValue}>{editData.building || unit.building || 'N/A'}</span>
                            )}
                        </div>

                        <div className={styles.unitNoSection}>
                            <label>Unit Number</label>
                            {isEditMode ? (
                                <div className={styles.fieldWithIcon}>
                                    <input
                                        type="text"
                                        value={editData.unit_no}
                                        onChange={(e) => handleFieldChange('unit_no', e.target.value)}
                                        className={styles.editInput}
                                    />
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                </div>
                            ) : (
                                <span className={styles.unitNoValue}>{editData.unit_no || unit.unit_no || 'N/A'}</span>
                            )}
                        </div>

                        <div className={styles.floorSection}>
                            <label>Floor</label>
                            {isEditMode ? (
                                <div className={styles.fieldWithIcon}>
                                    <input
                                        type="text"
                                        value={editData.floor}
                                        onChange={(e) => handleFieldChange('floor', e.target.value)}
                                        className={styles.editInput}
                                    />
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                </div>
                            ) : (
                                <span className={styles.floorValue}>{editData.floor || unit.floor || 'N/A'}</span>
                            )}
                        </div>

                        <div className={styles.descriptionSection}>
                            <label>Description</label>
                            {isEditMode ? (
                                <div className={styles.fieldWithIcon}>
                                    <textarea
                                        value={editData.description}
                                        onChange={(e) => handleFieldChange('description', e.target.value)}
                                        className={styles.editTextarea}
                                        rows="3"
                                    />
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                </div>
                            ) : (
                                <p className={styles.descriptionText}>{editData.description || unit.description || 'N/A'}</p>
                            )}
                        </div>
                        <div className={styles.availabilitySection}>
                            <label>Availability</label>
                            <span className={styles.availabilityValue}>
                                {editData.is_available !== undefined ? (editData.is_available ? 'Available' : 'Unavailable') : (unit.is_available !== undefined ? (unit.is_available ? 'Available' : 'Unavailable') : (unit.status === 'available' ? 'Available' : 'Unavailable'))}
                            </span>
                        </div>

                        {/* Customer/Group Information Section - show for unavailable units */}
                        {!unit?.is_available && hasShowCustomer && (unit?.user_id || unit?.group_name) && (
                            <div className={styles.customerSection}>
                                {/* Individual Customer */}
                                {unit?.user_id && !unit?.group_name && (
                                    <>
                                        <label>Assigned Customer</label>
                                        {loadingCustomer ? (
                                            <div className={styles.customerLoading}>
                                                <span>Loading customer details...</span>
                                            </div>
                                        ) : customerError ? (
                                            <div className={styles.customerError}>
                                                <span>{customerError}</span>
                                            </div>
                                        ) : (
                                            <div className={styles.customerInfo}>
                                                <button
                                                    className={styles.viewProfileButton}
                                                    onClick={() => handleViewCustomerProfile(unit?.user_id)}
                                                >
                                                    View Profile
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Group Information */}
                                {unit?.group_name && (
                                    <>
                                        <label>Assigned Group</label>
                                        <div className={styles.groupInfo}>
                                            <div className={styles.groupHeader}>
                                                <h4 className={styles.groupName}>{unit.group_name}</h4>
                                                <span className={styles.groupSwagId}>Group SWAG ID: {unit.group_swag_id}</span>
                                            </div>

                                            {unit.users && unit.users.length > 0 && (
                                                <div className={styles.groupMembers}>
                                                    <label>Group Members ({unit.users.length})</label>
                                                    <div className={styles.membersList}>
                                                        {unit.users.map((user, index) => (
                                                            <div key={user.user_id || index} className={styles.memberItem}>
                                                                <div className={styles.memberInfo}>
                                                                    <div className={styles.memberName}>{user.name}</div>
                                                                    <div className={styles.memberEmail}>{user.email}</div>
                                                                    <div className={styles.memberSwagId}>SWAG ID: {user.swag_id}</div>
                                                                </div>
                                                                <button
                                                                    className={styles.viewCustomerButton}
                                                                    onClick={() => handleViewCustomerProfile(user.user_id)}
                                                                    title={`View ${user.name}'s Profile`}
                                                                >
                                                                    View Customer
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        {isEditMode ? (
                            <div className={styles.editActions}>
                                <button
                                    className={styles.cancelButton}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={styles.saveButton}
                                    onClick={handleSave}
                                >
                                    Save
                                </button>
                            </div>
                        ) : (
                            <div className={styles.actionButtons}>
                                {unit.is_available && hasAddCustomerUnit && (
                                    <button
                                        className={styles.assignCustomerButton}
                                        onClick={handleAssignCustomer}
                                    >
                                        Add Customer
                                    </button>
                                )}
                                { !unit?.is_available && (
                                    <>
                                        {hasUpdateInstallment && (
                                            <button
                                                className={styles.viewProfileButton}
                                                onClick={() => navigate(`/unit/${unit.id}/update-installments`)}
                                            >
                                                Update Installments
                                            </button>
                                        )}
                                        {
                                            hasRemoveCustomerUnit && (
                                                <button
                                            className={`${styles.viewProfileButton} ${isUnlinking ? styles.disabled : ''}`}
                                            style={{
                                                background: '#F34A58',
                                                color: '#edededff'
                                            }}
                                            onClick={handleUnlinkCustomer}
                                            disabled={isUnlinking}
                                        >
                                            {isUnlinking ? (
                                                <>

                                                    Unlinking...
                                                </>
                                            ) : (
                                                <>
                                                    Unlink Customer
                                                </>
                                            )}
                                        </button>
                                            )
                                        }
                                    </>
                                )}
                                {unlinkError && (
                                    <div className={styles.errorMessage}>
                                        {unlinkError}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.rightContent}>
                    <div className={styles.unitImages}>
                        <h3>Unit Images</h3>
                        <div className={styles.imageGrid}>
                            {images.map(image => (
                                <div key={image.id} className={styles.imageItem}>
                                    <img src={image.src} alt={image.alt} />
                                    {isEditMode && (
                                        <button
                                            className={styles.removeButton}
                                            onClick={() => removeImage(image.id)}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        {isEditMode && (
                            <button
                                className={styles.addImageButton}
                                onClick={addImage}
                            >
                                Add Image
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnitDetails; 